import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { trackAssignmentEvent } from '../../../../actions/tracking';
import { lookup } from '../../../../actions/vehicle';
import { ExchangeTypes, useDataExchangeStatus } from '../../../../hooks/exchange-status';
import { getMarket } from '../../../../i18n';
import type { RootState } from '../../../../reducers';
import { getTokenId } from '../../../../selectors/auth';
import { assignmentEvents } from '../../../../shared/tracking';
import Alert from '../../../../styled-components/components/Alert';
import Content from '../../../../styled-components/components/Content';
import Modal from '../../../../styled-components/components/Modal';
import { indexedDbAssignmentClear, indexedDbAssignmentSet } from '../../../../utils/indexedDb';
import { getVehicleLookupResult } from '../../../../utils/reselectors';
import { resolveAssignmentCreatePartialPath, resolveAssignmentRoute } from '../../../utils';
import { AssignmentDto } from '../../utils';
import reducer, {
  SET_BLUR,
  SET_BLUR_NUMBER,
  type SET_BLUR_NUMBER_TYPE,
  type SET_BLUR_TYPE,
  SET_CHANGE,
  SET_CHANGE_NUMBER,
  type SET_CHANGE_NUMBER_TYPE,
  type SET_CHANGE_TYPE,
  RESET,
  initialState,
} from '../reducer';
import WidgetFullWidth from './WidgetFullWidth';
import WidgetProductPage from './WidgetProductPage';

export interface AssignmentWidgetVariantProps {
  onProceed: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onChangeNumber: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlurNumber: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  errorRegistrationNumber: boolean;
  errorOdometerReadingValue: boolean;
  disabled: boolean;
  loading?: boolean;
  registrationNumber: string;
  odometerReadingValue: number | null;
  maxOdometerReadingValue: number;
}

interface TrackingContextFooter {
  type: 'widget';
  placement: 'footer';
}

interface TrackingContextProductPage {
  type: 'widget';
  placement: 'productPage';
}

type TrackingContext = TrackingContextFooter | TrackingContextProductPage;

interface AssignmentWidgetProps {
  variant: 'productPage' | 'fullWidth';
  trackingContext: TrackingContext;
}

const AssignmentWidget = ({ variant, trackingContext }: AssignmentWidgetProps) => {
  const { t } = useTranslation(['assignment', 'global', 'validation']);
  const navigate = useNavigate();
  const requestStatus = useSelector<RootState, number>((s) => s.vehicles.requestStatus);
  const responseStatus = useSelector<RootState, number>((s) => s.vehicles.responseStatus);
  const vehicle = useSelector(getVehicleLookupResult);
  const marketCode = getMarket().marketCode;
  const dispatch = useDispatch();
  const [state, dispatchLocal] = useReducer(reducer, initialState());
  const requestGet = useDataExchangeStatus(ExchangeTypes.Fetching, [requestStatus, responseStatus]);
  const id = useSelector(getTokenId) || 'anonymous';

  const actions = bindActionCreators(
    {
      lookup,
      trackAssignmentEvent,
    },
    dispatch,
  );

  const onBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) =>
      dispatchLocal({
        type: SET_BLUR,
        name: e.currentTarget.name as SET_BLUR_TYPE['name'],
      }),
    [],
  );

  const onChangeString = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      dispatchLocal({
        type: SET_CHANGE,
        name: e.currentTarget.name as SET_CHANGE_TYPE['name'],
        value: e.currentTarget.value.toUpperCase(),
      }),
    [],
  );

  const onChangeNumber = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    dispatchLocal({
      type: SET_CHANGE_NUMBER,
      name: e.currentTarget.name as SET_CHANGE_NUMBER_TYPE['name'],
      value: Number.parseInt(e.currentTarget.value.replace(/\D/, ''), 10),
    });
  }, []);

  const onBlurNumber = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    dispatchLocal({
      type: SET_BLUR_NUMBER,
      name: e.currentTarget.name as SET_BLUR_NUMBER_TYPE['name'],
    });
  }, []);

  const onProceed = () => {
    actions.lookup(state.value.registration_number);
  };

  const onRevert = () => {
    indexedDbAssignmentClear();
    dispatchLocal({ type: RESET });
  };

  // Success
  useEffect(() => {
    const init = async () => {
      const dto = AssignmentDto.fromLookup(vehicle);
      dto.vehicle.odometerReading = {
        value: state.value.odometerReadingValue ?? 0,
        unit: marketCode === 'SE' ? 'mil' : 'km',
      };

      dto.interestedInTradeIn = false;

      await indexedDbAssignmentClear();
      await indexedDbAssignmentSet({ ...dto, userId: id });
      actions.trackAssignmentEvent(assignmentEvents.INIT, dto.vehicle, {
        context: trackingContext,
      });
      navigate(`/${resolveAssignmentRoute()}/${resolveAssignmentCreatePartialPath()}`);
    };

    if (requestGet.successful) {
      init();
    }
  }, [requestGet.successful]);

  // Error
  const [failedModal, setFailedModal] = useState(false);

  useEffect(() => {
    if (requestGet.failed) {
      setFailedModal(true);
    }
  }, [requestGet.failed]);

  const onRevertAssignmentAfterFailedFetch = () => {
    setFailedModal(false);
    onRevert();
  };

  const maxOdometerReadingValue = marketCode === 'NO' ? 800000 : 80000;
  const validateOdometerReadingValue = (odometerReadingValue: number | null) => {
    return !(
      odometerReadingValue === null ||
      Number.isNaN(odometerReadingValue) ||
      odometerReadingValue <= 0 ||
      odometerReadingValue >= maxOdometerReadingValue
    );
  };

  const errorOdometerReadingValue = !validateOdometerReadingValue(state.value.odometerReadingValue);

  return (
    <>
      {failedModal && (
        <Modal
          heading={t('vehicleNotApproved', { ns: 'assignment' }) || undefined}
          onClose={onRevertAssignmentAfterFailedFetch}
        >
          <Content>
            <p>
              {t('vehicleNotApprovedBody', {
                ns: 'assignment',
                brandName: t('brandName', { ns: 'global' }),
              })}
            </p>
          </Content>
        </Modal>
      )}
      {variant === 'productPage' ? (
        <WidgetProductPage
          registrationNumber={state.value.registration_number}
          errorRegistrationNumber={
            state.interact.registration_number && !state.validation.registration_number
          }
          odometerReadingValue={state.value.odometerReadingValue}
          maxOdometerReadingValue={maxOdometerReadingValue}
          errorOdometerReadingValue={
            (state.interact.odometerReadingValue && !state.validation.odometerReadingValue) ||
            (state.validation.odometerReadingValue && errorOdometerReadingValue)
          }
          onBlur={onBlur}
          onBlurNumber={onBlurNumber}
          onChangeNumber={onChangeNumber}
          onChange={onChangeString}
          onProceed={onProceed}
          disabled={
            !state.validation.registration_number ||
            !state.validation.odometerReadingValue ||
            (state.validation.odometerReadingValue &&
              state.validation.registration_number &&
              errorOdometerReadingValue)
          }
          loading={requestGet.pending}
        />
      ) : variant === 'fullWidth' ? (
        <WidgetFullWidth
          registrationNumber={state.value.registration_number}
          errorRegistrationNumber={
            state.interact.registration_number && !state.validation.registration_number
          }
          odometerReadingValue={state.value.odometerReadingValue}
          maxOdometerReadingValue={maxOdometerReadingValue}
          errorOdometerReadingValue={
            (state.interact.odometerReadingValue && !state.validation.odometerReadingValue) ||
            (state.validation.odometerReadingValue && errorOdometerReadingValue)
          }
          onBlur={onBlur}
          onBlurNumber={onBlurNumber}
          onChangeNumber={onChangeNumber}
          onChange={onChangeString}
          onProceed={onProceed}
          disabled={
            !state.validation.registration_number ||
            !state.validation.odometerReadingValue ||
            (state.validation.odometerReadingValue &&
              state.validation.registration_number &&
              errorOdometerReadingValue)
          }
          loading={requestGet.pending}
        />
      ) : (
        <Alert tone="error">{t('unknownError', { ns: 'validation ' })}</Alert>
      )}
    </>
  );
};

export default AssignmentWidget;
